import { Layers } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui";
import { EmptyState } from "@/components/common";
import { formatCurrency } from "@/lib/utils";
import type { CategoryBreakdownRow } from "@/types";

interface CategoryBreakdownCardProps {
  rows: CategoryBreakdownRow[];
}

export function CategoryBreakdownCard({ rows }: CategoryBreakdownCardProps) {
  const totalRevenue = rows.reduce((sum, row) => sum + row.revenue, 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Layers className="h-5 w-5" />
          Category mix
        </CardTitle>
        <CardDescription>Revenue share by category</CardDescription>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <EmptyState
            icon={Layers}
            title="No category data"
            description="Once you sell items linked to categories they'll show here"
          />
        ) : (
          <ul className="space-y-3">
            {rows.map((row) => {
              const share = totalRevenue > 0 ? row.revenue / totalRevenue : 0;

              return (
                <li key={row.categoryId} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">{row.categoryName}</span>
                    <span className="text-muted-foreground">
                      {formatCurrency(row.revenue)} · {row.quantity} items
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-muted">
                    <div
                      className="h-2 rounded-full bg-primary"
                      style={{ width: `${(share * 100).toFixed(1)}%` }}
                    />
                  </div>
                  <p className="text-right text-[10px] text-muted-foreground">
                    {(share * 100).toFixed(1)}% of revenue
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
